import { html, type TemplateResult } from 'lit';
import type { ShellNavItem } from '../../../../../app-context/domain/shellNavItems.ts';
import {
  SHELL_SYNC_NAV_PATH,
  shellNavItemAccessibleLabel,
  shellSyncPendingBadgeTemplate,
} from '../../../shellNavPendingSyncBadge.ts';
import { shellNavPathname } from '@src/modules/shared/app-context/domain/shellNavRoutes.ts';

const ICON_DASHBOARD = html`<svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path d="M4 8h4V4H4v4zm6 12h4v-4h-4v4zm-6 0h4v-4H4v4zm0-6h4v-4H4v4zm6 0h4v-4h-4v4zm6-10v4h4V4h-4zm-6 4h4V4h-4v4zm6 6h4v-4h-4v4zm0 6h4v-4h-4v4z" />
</svg>`;

const ICON_PLACE = html`<svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path
    d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"
  />
</svg>`;

const ICON_RECEIPT = html`<svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path
    d="M18 17H6v-2h12v2zm0-4H6v-2h12v2zm0-4H6V7h12v2zM3 22l1.5-1.5L6 22l1.5-1.5L9 22l1.5-1.5L12 22l1.5-1.5L15 22l1.5-1.5L18 22l1.5-1.5L21 22V2l-1.5 1.5L18 2l-1.5 1.5L15 2l-1.5 1.5L12 2l-1.5 1.5L9 2 7.5 3.5 6 2 4.5 3.5 3 2v20z"
  />
</svg>`;

const ICON_SYNC = html`<svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path
    d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46C19.54 15.03 20 13.57 20 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74C4.46 8.97 4 10.43 4 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"
  />
</svg>`;

const ICON_OBSERVABILIDAD = html`<svg class="h-6 w-6" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
  <path
    d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V5h14v14zM7 12h2v5H7v-5zm4-3h2v8h-2V9zm4-3h2v11h-2V6z"
  />
</svg>`;

const NAV_ICONS: Record<string, TemplateResult> = {
  '/': ICON_DASHBOARD,
  '/puntos-entrega': ICON_PLACE,
  '/redencion-bono': ICON_RECEIPT,
  '/sync': ICON_SYNC,
  '/observabilidad': ICON_OBSERVABILIDAD,
};
/** Nav link class. */


function navItemTitle(item: ShellNavItem, pendingSyncCount: number): string | undefined {
  if (shellNavPathname(item.path) !== SHELL_SYNC_NAV_PATH) return item.label;
  return pendingSyncCount > 0 ? shellNavItemAccessibleLabel(item.label, item.path, pendingSyncCount) : item.label;
}

function shellNavIconContent(item: ShellNavItem, pendingSyncCount: number): TemplateResult {
  const icon = NAV_ICONS[shellNavPathname(item.path)] ?? ICON_DASHBOARD;
  if (shellNavPathname(item.path) !== SHELL_SYNC_NAV_PATH) return icon;
  return html`${icon}${shellSyncPendingBadgeTemplate(pendingSyncCount)}`;
}

function navLinkClass(path: string, activePath: string, rail: boolean): string {
  const active = activePath === shellNavPathname(path);
  const base = rail
    ? 'touch-manipulation flex w-full flex-col items-center gap-1 rounded-md px-2 py-3 text-center md-sys-ripple outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--md-sys-color-primary)]'
    : 'touch-manipulation flex min-h-[3.5rem] min-w-0 flex-1 flex-col items-center justify-center gap-0.5 px-1 md-sys-ripple outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[color:var(--md-sys-color-primary)]';
  const tone = active ? '' : 'hover:bg-black/[0.04]';
  return `${base} ${tone}`.trim();
}
/** Sidebar component template. */


export function sidebarComponentTemplate(p: {
  navItems: readonly ShellNavItem[];
  activePath: string;
  pendingSyncCount: number;
  onNavClick: (e: Event, path: string) => void;
}): TemplateResult {
  return html`
    <aside
      class="fixed left-0 top-0 z-40 hidden h-full w-20 flex-col items-stretch border-r border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container)] pt-[max(0.75rem,env(safe-area-inset-top))] lg:flex"
      aria-label="Navegación principal"
    >
      <nav class="shell-rail-nav flex flex-1 flex-col gap-1 px-2">
        ${p.navItems.map(
          (item) => html`
            <a
              href="${item.path}"
              class=${navLinkClass(item.path, p.activePath, true)}
              aria-current=${p.activePath === shellNavPathname(item.path) ? 'page' : undefined}
              aria-label=${shellNavItemAccessibleLabel(item.label, item.path, p.pendingSyncCount)}
              title=${navItemTitle(item, p.pendingSyncCount)}
              @click=${(e: Event) => p.onNavClick(e, item.path)}
            >
              <span class="shell-nav-dest-icon-shell">${shellNavIconContent(item, p.pendingSyncCount)}</span>
              <span class="shell-nav-dest-label max-w-[4.5rem] text-center">${item.label}</span>
            </a>
          `,
        )}
      </nav>
    </aside>

    <nav
      class="shell-bottom-nav fixed bottom-0 left-0 right-0 z-50 flex min-h-20 items-stretch justify-around border-t border-[color:var(--md-sys-color-outline-variant)] bg-[color:var(--md-sys-color-surface-container)] pt-1 lg:hidden"
      style="box-shadow: var(--md-elevation-1); padding-bottom: env(safe-area-inset-bottom);"
      aria-label="Navegación inferior"
    >
      ${p.navItems.map(
        (item) => html`
          <a
            href="${item.path}"
            class=${navLinkClass(item.path, p.activePath, false)}
            aria-current=${p.activePath === shellNavPathname(item.path) ? 'page' : undefined}
            aria-label=${shellNavItemAccessibleLabel(item.label, item.path, p.pendingSyncCount)}
            title=${navItemTitle(item, p.pendingSyncCount)}
            @click=${(e: Event) => p.onNavClick(e, item.path)}
          >
            <span class="shell-nav-dest-icon-shell">${shellNavIconContent(item, p.pendingSyncCount)}</span>
            <span class="shell-nav-dest-label max-w-full truncate">${item.label}</span>
          </a>
        `,
      )}
    </nav>
  `;
}
